import { useQuery } from '@tanstack/react-query'
import { PartnerService } from '@duck/api-definitions'
import { apis } from '@/modules/apiClients'
import { useAuth } from './useAuth'

export const cohortsQueryKeys = {
  all: () => ['cohorts'] as const,
  user: () => ['cohorts', 'user'] as const
}

const fetchCohorts = async (): Promise<PartnerService.CohortListResponse> => {
  try {
    const response = await apis.partnerService.cohortsGet()
    return response.data
  } catch {
    return {
      data: [],
    }
  }
}

const fetchCohort = async (cohortId: string): Promise<PartnerService.CohortResponse> => {
  const response = await apis.partnerService.cohortsCohortIdGet(cohortId)
  return response.data
}

export const useCohorts = () => {
  const { isPartner } = useAuth()
  const query = useQuery({
    queryKey: cohortsQueryKeys.user(),
    queryFn: fetchCohorts,
    enabled: isPartner,
    staleTime: 30 * 60 * 1000, // 30 minutes
  })

  return {
    cohorts: query.data?.data ?? [],
    isLoading: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  } as const
}

export const useCohort = (cohortId: string | undefined) => {
  const { isPartner } = useAuth()
  
  const query = useQuery({
    queryKey: [...cohortsQueryKeys.all(), 'detail', cohortId],
    queryFn: async () => {
      if (!cohortId) {
        throw new Error('Cohort ID is required')
      }
      return fetchCohort(cohortId)
    },
    enabled: isPartner && !!cohortId,
    staleTime: 30 * 60 * 1000, // 30 minutes
  })

  return {
    cohort: query.data?.data,
    assignmentsDetails: query.data?.assignmentsDetails,
    publicAssignments: query.data?.publicAssignments,
    isLoading: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  } as const
}
